import { useState } from "react";
import { Camera, UtensilsCrossed, Rocket, CheckCircle2, ChevronRight, X } from "lucide-react";

// Guide « 3 étapes pour publier votre vitrine » — Nold Go.
// Affiché sur RestaurateurDashboard uniquement si la vitrine n'est pas encore publiée.
// Chaque étape est cliquable → bascule vers l'onglet correspondant.
// L'utilisateur peut masquer le guide (persistance localStorage).
export default function PublishGuide({ gate, onSwitchTab, onPublish, publishing }) {
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem("nold_publish_guide_hidden") === "1"; } catch { return false; }
  });

  if (!gate || gate.published || hidden || gate.admin_locked) return null;

  const hasCover = !!(gate.has_cover || gate.has_logo);
  const nbItems = gate.n_items_active || 0;
  const enoughItems = nbItems >= 3;
  const canPublish = !!gate.can_publish;

  const dismiss = () => {
    try { localStorage.setItem("nold_publish_guide_hidden", "1"); } catch {}
    setHidden(true);
  };

  const Step = ({ n, icon: Icon, title, desc, done, current, actionLabel, onClick, testId }) => (
    <button
      onClick={onClick}
      disabled={done}
      data-testid={testId}
      className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
        done
          ? "bg-emerald-50 border-emerald-200 cursor-default"
          : current
          ? "bg-white border-[#F26419] hover:border-[#D94D0E] shadow-sm"
          : "bg-white border-[#E2DFD8] hover:border-[#F26419]/60"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`shrink-0 w-10 h-10 rounded-xl grid place-items-center font-bold ${
            done ? "bg-emerald-500 text-white" : current ? "bg-[#F26419] text-white" : "bg-[#F0EBE1] text-[#75807A]"
          }`}
        >
          {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#75807A]">Étape {n}</div>
          <div className={`font-head font-bold text-base ${done ? "text-emerald-700 line-through" : "text-[#1A1A1A]"}`}>
            {title}
          </div>
          <div className="text-xs text-[#75807A] mt-0.5">{desc}</div>
        </div>
        {!done && (
          <div className={`shrink-0 text-xs font-semibold ${current ? "text-[#F26419]" : "text-[#75807A]"}`}>
            {actionLabel} <ChevronRight className="inline w-4 h-4 ml-0.5" />
          </div>
        )}
      </div>
    </button>
  );

  return (
    <div className="mt-4 rounded-3xl bg-gradient-to-br from-[#FFF3E6] to-[#FDF8F0] border-2 border-[#F26419]/30 p-5 relative" data-testid="publish-guide">
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 w-8 h-8 rounded-full grid place-items-center text-[#75807A] hover:bg-[#F26419]/10 hover:text-[#F26419]"
        aria-label="Masquer le guide"
        data-testid="publish-guide-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="pr-8">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#F26419] text-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider">
          <Rocket className="w-3.5 h-3.5" /> Bienvenue chez Nold Go
        </div>
        <h2 className="mt-2 font-head font-black text-xl sm:text-2xl text-[#1A1A1A]">
          3 étapes pour publier votre vitrine
        </h2>
        <p className="text-sm text-[#3B423E] mt-1">
          Complétez ces étapes puis publiez votre restaurant sur noldgo.com.
        </p>
      </div>

      <div className="mt-4 space-y-2.5">
        <Step
          n={1}
          icon={Camera}
          title="Ajouter votre photo principale"
          desc="Elle apparaît en bandeau sur votre vitrine publique."
          done={hasCover}
          current={!hasCover}
          actionLabel="Ajouter"
          onClick={() => onSwitchTab?.("profile")}
          testId="guide-step-photo"
        />
        <Step
          n={2}
          icon={UtensilsCrossed}
          title={`Ajoutez vos plats (${nbItems}/100)`}
          desc="Minimum 3, maximum 100 plats. Nom, prix, description et photo pour chacun."
          done={enoughItems}
          current={hasCover && !enoughItems}
          actionLabel={enoughItems ? "Modifier" : "Ajouter"}
          onClick={() => onSwitchTab?.("menu")}
          testId="guide-step-menu"
        />
        <Step
          n={3}
          icon={Rocket}
          title="Publier votre vitrine sur noldgo.com"
          desc="Un clic — vos clients peuvent commander immédiatement."
          done={false}
          current={canPublish}
          actionLabel={publishing ? "Publication…" : "Publier"}
          onClick={canPublish && !publishing ? onPublish : undefined}
          testId="guide-step-publish"
        />
      </div>

      {!canPublish && (
        <p className="mt-3 text-xs text-[#75807A] italic">
          Complétez les étapes 1 et 2 pour débloquer la publication.
        </p>
      )}
    </div>
  );
}
